/**
 * Stock calculation utilities for the frontend
 *
 * These functions provide centralized logic for calculating and displaying stock information.
 * Use these functions to ensure consistent stock calculations across the application.
 */

/**
 * Calculate confirmed stock from stock history
 * Confirmed stock is the quantity from the most recent stock count
 *
 * @param {Array} stockHistory - Array of stock count items, should be sorted by date DESC
 * @returns {Object} { quantity: number, date: string|null, stockCountName: string|null }
 */
export const getConfirmedStock = (stockHistory) => {
  if (!stockHistory || stockHistory.length === 0) {
    return {
      quantity: 0,
      date: null,
      stockCountName: null,
    };
  }

  const latest = stockHistory[0];
  return {
    quantity: latest.quantity || 0,
    date: latest.date,
    stockCountName: latest.stock_count_name,
  };
};

/**
 * Calculate estimated stock
 * Estimated stock = Confirmed stock + Purchases since last count - Sales since last count
 *
 * Note: This is a client-side approximation. For accurate calculations,
 * use the /products/{id}/stock-info API endpoint which uses server-side logic.
 *
 * @param {number} confirmedStock - The confirmed stock quantity
 * @param {Array} purchases - Array of purchase items since last stock count
 * @param {Array} sales - Array of sale items since last stock count
 * @returns {number} Estimated stock quantity
 */
export const calculateEstimatedStock = (confirmedStock, purchases = [], sales = []) => {
  const purchaseTotal = purchases.reduce((sum, p) => sum + (p.quantity || 0), 0);
  const salesTotal = sales.reduce((sum, s) => sum + (s.quantity || 0), 0);

  return confirmedStock + purchaseTotal - salesTotal;
};

/**
 * Get comprehensive stock information from API response
 *
 * @param {Object} stockInfo - Stock info object from /products/{id}/stock-info endpoint
 * @returns {Object} Formatted stock information
 */
export const formatStockInfo = (stockInfo) => {
  if (!stockInfo) {
    return {
      confirmedStock: 0,
      estimatedStock: 0,
      lastCountDate: null,
      lastCountName: null,
      purchasesSinceCount: 0,
      salesSinceCount: 0,
      hasStockCount: false,
    };
  }

  return {
    confirmedStock: stockInfo.confirmed_stock || 0,
    estimatedStock: stockInfo.estimated_stock || 0,
    lastCountDate: stockInfo.last_count_date,
    lastCountName: stockInfo.last_count_name,
    purchasesSinceCount: stockInfo.purchases_since_count || 0,
    salesSinceCount: stockInfo.sales_since_count || 0,
    hasStockCount: stockInfo.last_count_date !== null,
  };
};

/**
 * Determine stock status based on estimated stock level
 *
 * @param {number} estimatedStock - The estimated stock quantity
 * @param {Object} thresholds - Optional thresholds { low: number, critical: number }
 * @returns {string} Status: 'ok', 'low', or 'critical'
 */
export const getStockStatus = (estimatedStock, thresholds = { low: 10, critical: 3 }) => {
  if (estimatedStock <= 0) return "out";
  if (estimatedStock <= thresholds.critical) return "critical";
  if (estimatedStock <= thresholds.low) return "low";
  return "ok";
};

/**
 * Get CSS class for stock status badge
 *
 * @param {string} status - Stock status from getStockStatus()
 * @returns {string} Tailwind CSS classes for badge
 */
export const getStockStatusClasses = (status) => {
  const baseClasses = "px-2 py-1 rounded text-xs font-semibold";

  switch (status) {
    case "out":
      return `${baseClasses} bg-red-100 text-red-800`;
    case "critical":
      return `${baseClasses} bg-orange-100 text-orange-800`;
    case "low":
      return `${baseClasses} bg-yellow-100 text-yellow-800`;
    case "ok":
      return `${baseClasses} bg-green-100 text-green-800`;
    default:
      return `${baseClasses} bg-gray-100 text-gray-800`;
  }
};

/**
 * Get human-readable stock status label
 *
 * @param {string} status - Stock status from getStockStatus()
 * @returns {string} Human-readable label
 */
export const getStockStatusLabel = (status) => {
  switch (status) {
    case "out":
      return "Uitverkocht";
    case "critical":
      return "Kritiek laag";
    case "low":
      return "Laag";
    case "ok":
      return "Op voorraad";
    default:
      return "Onbekend";
  }
};
